import type { GameSettings, ObjectiveProgress, SaveData } from '../core/types';
import { CAMPAIGN, WORLDS } from '../levels/campaign';
import { PIECE_LABELS, SPECIAL_LABELS } from '../render/palette';

export type ScreenId =
  | 'main'
  | 'map'
  | 'settings'
  | 'credits'
  | 'hud'
  | 'pause'
  | 'win'
  | 'lose'
  | 'tutorial';

export interface UiCallbacks {
  onPlay: (levelId: number) => void;
  onContinue: () => void;
  onOpenMap: () => void;
  onOpenSettings: () => void;
  onOpenCredits: () => void;
  onBackMain: () => void;
  onPause: () => void;
  onResume: () => void;
  onRestart: () => void;
  onNextLevel: () => void;
  onQuitLevel: () => void;
  onHint: () => void;
  onSettingsChange: (s: Partial<GameSettings>) => void;
  onResetProgress: () => void;
  onTutorialDismiss: () => void;
}

export class UI {
  private root: HTMLElement;
  private screens = new Map<ScreenId, HTMLElement>();
  private cb: UiCallbacks;
  private save: SaveData;

  constructor(root: HTMLElement, save: SaveData, cb: UiCallbacks) {
    this.root = root;
    this.save = save;
    this.cb = cb;
    this.build();
    this.applySettingsClass(save.settings);
  }

  private el(html: string): HTMLElement {
    const t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild as HTMLElement;
  }

  private build(): void {
    this.root.innerHTML = '';

    // Main
    const main = this.el(`
      <div class="screen active" data-screen="main">
        <h1 class="brand">Aetherloom</h1>
        <p class="tagline">Weave the luminous essences</p>
        <div class="panel">
          <div class="btn-row">
            <button class="btn primary" data-act="continue">Continue</button>
            <button class="btn" data-act="map">World Map</button>
            <button class="btn ghost" data-act="settings">Settings</button>
            <button class="btn ghost" data-act="credits">Credits</button>
          </div>
        </div>
      </div>
    `);
    this.screens.set('main', main);
    this.root.appendChild(main);

    // Map
    const map = this.el(`
      <div class="screen" data-screen="map">
        <h1 class="brand" style="font-size:2.2rem">Constellation Map</h1>
        <p class="tagline">Choose a loom to weave</p>
        <div class="panel world-map" data-map></div>
        <div class="btn-row"><button class="btn ghost" data-act="back">Back</button></div>
      </div>
    `);
    this.screens.set('map', map);
    this.root.appendChild(map);

    // Settings
    const settings = this.el(`
      <div class="screen" data-screen="settings">
        <h1 class="brand" style="font-size:2rem">Settings</h1>
        <div class="panel settings-grid">
          <label>Master volume<input type="range" min="0" max="1" step="0.05" data-set="masterVolume"/></label>
          <label>Music<input type="range" min="0" max="1" step="0.05" data-set="musicVolume"/></label>
          <label>Effects<input type="range" min="0" max="1" step="0.05" data-set="sfxVolume"/></label>
          <label>UI scale<input type="range" min="0.85" max="1.25" step="0.05" data-set="uiScale"/></label>
          <label>Quality
            <select data-set="quality">
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label class="check"><input type="checkbox" data-set="colorblind"/> Colorblind patterns</label>
          <label class="check"><input type="checkbox" data-set="highContrast"/> High contrast</label>
          <label class="check"><input type="checkbox" data-set="reducedMotion"/> Reduced motion</label>
          <label class="check"><input type="checkbox" data-set="reducedFlash"/> Reduced flashes</label>
          <label class="check"><input type="checkbox" data-set="hints"/> Hints</label>
          <div class="btn-row">
            <button class="btn danger" data-act="reset">Reset progress</button>
            <button class="btn" data-act="back">Back</button>
          </div>
        </div>
      </div>
    `);
    this.screens.set('settings', settings);
    this.root.appendChild(settings);

    // Credits
    const credits = this.el(`
      <div class="screen" data-screen="credits">
        <h1 class="brand" style="font-size:2rem">Credits</h1>
        <div class="panel">
          <p class="muted">Aetherloom is an original match-3 adventure.</p>
          <p>Procedural essences · Celestial looms · Handcrafted campaign</p>
          <p class="muted" style="margin-top:1rem">Built with Three.js, TypeScript & Vite</p>
          <p class="muted">All art, audio, and design are original.</p>
          <div class="btn-row"><button class="btn" data-act="back">Back</button></div>
        </div>
      </div>
    `);
    this.screens.set('credits', credits);
    this.root.appendChild(credits);

    // HUD
    const hud = this.el(`
      <div class="hud" data-screen="hud">
        <div class="hud-top">
          <div class="hud-chip"><span class="label">Moves</span><span class="value" data-hud="moves">0</span></div>
          <div class="objectives" data-hud="objectives"></div>
          <div class="hud-chip"><span class="label">Score</span><span class="value" data-hud="score">0</span></div>
        </div>
        <div class="hud-actions">
          <button class="btn ghost" data-act="pause">Pause</button>
          <button class="btn ghost" data-act="hint">Hint</button>
          <button class="btn ghost" data-act="restart">Restart</button>
        </div>
      </div>
    `);
    this.screens.set('hud', hud);
    this.root.appendChild(hud);

    // Pause
    const pause = this.el(`
      <div class="screen" data-screen="pause">
        <div class="panel">
          <h2 class="overlay-title">Paused</h2>
          <div class="btn-row">
            <button class="btn primary" data-act="resume">Resume</button>
            <button class="btn" data-act="restart">Restart</button>
            <button class="btn" data-act="settings">Settings</button>
            <button class="btn ghost" data-act="quit">Quit to Map</button>
          </div>
        </div>
      </div>
    `);
    this.screens.set('pause', pause);
    this.root.appendChild(pause);

    // Win
    const win = this.el(`
      <div class="screen" data-screen="win">
        <div class="panel">
          <h2 class="overlay-title">Loom Complete</h2>
          <div class="stars-big" data-win="stars">★★★</div>
          <p class="muted">Score <strong data-win="score">0</strong> · Moves left <strong data-win="moves">0</strong></p>
          <div class="btn-row">
            <button class="btn primary" data-act="next">Next Level</button>
            <button class="btn" data-act="restart">Replay</button>
            <button class="btn ghost" data-act="quit">Map</button>
          </div>
        </div>
      </div>
    `);
    this.screens.set('win', win);
    this.root.appendChild(win);

    // Lose
    const lose = this.el(`
      <div class="screen" data-screen="lose">
        <div class="panel">
          <h2 class="overlay-title">Threads Frayed</h2>
          <p class="muted">The loom stalled before the pattern was complete.</p>
          <p class="muted">Score <strong data-lose="score">0</strong></p>
          <div class="btn-row">
            <button class="btn primary" data-act="restart">Try Again</button>
            <button class="btn ghost" data-act="quit">Map</button>
          </div>
        </div>
      </div>
    `);
    this.screens.set('lose', lose);
    this.root.appendChild(lose);

    // Tutorial
    const tutorial = this.el(`
      <div class="screen" data-screen="tutorial">
        <div class="panel">
          <h2 class="overlay-title" style="font-size:1.8rem" data-tut="title">Tutorial</h2>
          <ul class="tutorial-box" data-tut="list"></ul>
          <div class="btn-row"><button class="btn primary" data-act="tut-ok">Got it</button></div>
        </div>
      </div>
    `);
    this.screens.set('tutorial', tutorial);
    this.root.appendChild(tutorial);

    this.root.addEventListener('click', (e) => {
      const t = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
      if (!t) return;
      const act = t.dataset.act;
      switch (act) {
        case 'continue':
          this.cb.onContinue();
          break;
        case 'map':
          this.cb.onOpenMap();
          break;
        case 'settings':
          this.cb.onOpenSettings();
          break;
        case 'credits':
          this.cb.onOpenCredits();
          break;
        case 'back':
          this.cb.onBackMain();
          break;
        case 'pause':
          this.cb.onPause();
          break;
        case 'resume':
          this.cb.onResume();
          break;
        case 'restart':
          this.cb.onRestart();
          break;
        case 'next':
          this.cb.onNextLevel();
          break;
        case 'quit':
          this.cb.onQuitLevel();
          break;
        case 'hint':
          this.cb.onHint();
          break;
        case 'reset':
          if (confirm('Reset all progress?')) this.cb.onResetProgress();
          break;
        case 'tut-ok':
          this.cb.onTutorialDismiss();
          break;
      }
    });

    settings.querySelectorAll('[data-set]').forEach((node) => {
      const el = node as HTMLInputElement | HTMLSelectElement;
      const key = el.dataset.set as keyof GameSettings;
      el.addEventListener('change', () => {
        let value: unknown;
        if (el instanceof HTMLInputElement && el.type === 'checkbox') value = el.checked;
        else if (el instanceof HTMLInputElement && el.type === 'range') value = Number(el.value);
        else value = el.value;
        this.cb.onSettingsChange({ [key]: value } as Partial<GameSettings>);
      });
      el.addEventListener('input', () => {
        if (el instanceof HTMLInputElement && el.type === 'range') {
          this.cb.onSettingsChange({ [key]: Number(el.value) } as Partial<GameSettings>);
        }
      });
    });

    this.syncSettingsForm();
    this.renderMap();
  }

  updateSave(save: SaveData): void {
    this.save = save;
    this.applySettingsClass(save.settings);
    this.syncSettingsForm();
    this.renderMap();
  }

  private applySettingsClass(s: GameSettings): void {
    document.documentElement.style.setProperty('--ui-scale', String(s.uiScale));
    document.body.classList.toggle('high-contrast', s.highContrast);
    document.body.classList.toggle('reduced-motion', s.reducedMotion);
  }

  private syncSettingsForm(): void {
    const s = this.save.settings;
    const root = this.screens.get('settings')!;
    (root.querySelector('[data-set="masterVolume"]') as HTMLInputElement).value = String(s.masterVolume);
    (root.querySelector('[data-set="musicVolume"]') as HTMLInputElement).value = String(s.musicVolume);
    (root.querySelector('[data-set="sfxVolume"]') as HTMLInputElement).value = String(s.sfxVolume);
    (root.querySelector('[data-set="uiScale"]') as HTMLInputElement).value = String(s.uiScale);
    (root.querySelector('[data-set="quality"]') as HTMLSelectElement).value = s.quality;
    (root.querySelector('[data-set="colorblind"]') as HTMLInputElement).checked = s.colorblind;
    (root.querySelector('[data-set="highContrast"]') as HTMLInputElement).checked = s.highContrast;
    (root.querySelector('[data-set="reducedMotion"]') as HTMLInputElement).checked = s.reducedMotion;
    (root.querySelector('[data-set="reducedFlash"]') as HTMLInputElement).checked = s.reducedFlash;
    (root.querySelector('[data-set="hints"]') as HTMLInputElement).checked = s.hints;
  }

  renderMap(): void {
    const host = this.screens.get('map')!.querySelector('[data-map]')!;
    host.innerHTML = '';
    for (const world of WORLDS) {
      const unlockedWorld =
        !world.unlockAfter || this.save.unlockedLevel > world.unlockAfter ||
        this.save.levelStars[world.unlockAfter];
      const section = this.el(`
        <div class="world-section">
          <h2>${world.name}</h2>
          <p>${world.subtitle}${unlockedWorld ? '' : ' — locked'}</p>
          <div class="level-grid"></div>
        </div>
      `);
      const grid = section.querySelector('.level-grid')!;
      for (const id of world.levelIds) {
        const level = CAMPAIGN.find((l) => l.id === id)!;
        const open = id <= this.save.unlockedLevel;
        const stars = this.save.levelStars[id] ?? 0;
        const btn = document.createElement('button');
        btn.className = 'level-btn';
        btn.disabled = !open;
        btn.innerHTML = `${id}<div class="stars">${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}</div>`;
        btn.title = level.name;
        btn.addEventListener('click', () => this.cb.onPlay(id));
        grid.appendChild(btn);
      }
      host.appendChild(section);
    }
  }

  show(id: ScreenId): void {
    for (const [key, el] of this.screens) {
      if (key === 'hud') {
        const on = id === 'hud' || id === 'pause' || id === 'tutorial' || id === 'win' || id === 'lose';
        el.classList.toggle('active', on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        continue;
      }
      const on = key === id;
      el.classList.toggle('active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
  }

  showHudOnly(): void {
    for (const [key, el] of this.screens) {
      if (key === 'hud') el.classList.add('active');
      else el.classList.remove('active');
    }
  }

  updateHud(moves: number, score: number, objectives: ObjectiveProgress[]): void {
    const hud = this.screens.get('hud')!;
    (hud.querySelector('[data-hud="moves"]') as HTMLElement).textContent = String(moves);
    (hud.querySelector('[data-hud="score"]') as HTMLElement).textContent = score.toLocaleString();
    const box = hud.querySelector('[data-hud="objectives"]')!;
    box.innerHTML = objectives
      .map((o) => {
        let label = o.label;
        if (o.type === 'collect' && o.kind) label = `${PIECE_LABELS[o.kind]} ${o.current}/${o.target}`;
        else if (o.special) label = `${SPECIAL_LABELS[o.special] ?? o.label} ${o.current}/${o.target}`;
        else label = `${o.label.replace(/\\d.*/, '').trim()} ${o.current}/${o.target}`;
        // simpler:
        label = `${o.label}: ${o.current}/${o.target}`;
        return `<div class="obj-item ${o.done ? 'done' : ''}">${label}</div>`;
      })
      .join('');
  }

  showWin(score: number, moves: number, stars: number): void {
    const w = this.screens.get('win')!;
    (w.querySelector('[data-win="score"]') as HTMLElement).textContent = score.toLocaleString();
    (w.querySelector('[data-win="moves"]') as HTMLElement).textContent = String(moves);
    (w.querySelector('[data-win="stars"]') as HTMLElement).textContent =
      '★'.repeat(stars) + '☆'.repeat(3 - stars);
    this.show('win');
  }

  showLose(score: number): void {
    const w = this.screens.get('lose')!;
    (w.querySelector('[data-lose="score"]') as HTMLElement).textContent = score.toLocaleString();
    this.show('lose');
  }

  showTutorial(title: string, lines: string[]): void {
    const t = this.screens.get('tutorial')!;
    (t.querySelector('[data-tut="title"]') as HTMLElement).textContent = title;
    (t.querySelector('[data-tut="list"]') as HTMLElement).innerHTML = lines
      .map((l) => `<li>${l}</li>`)
      .join('');
    this.show('tutorial');
  }
}
